/**
 * Тайлан — прототипийн `11 Reports.dc.html`.
 *
 * Бүтэц: борлуулалт, ашиг (шүүлтүүртэй) → сарын нэгтгэл → тайлангийн карт.
 * Хугацааны шүүлтүүр ЗӨВХӨН дээд блокт — доорх хэсгүүд хугацаанаас хамаарахгүй.
 *
 * ⚠️ Прототипийн сарын хүснэгтэд «Зарсан · Орлого · Зардал · Ашиг»
 *    багана байсан — бүгд `orders` дээр тулгуурладаг. «Цуглуулсан» нь
 *    цуглуулсан огноогоор. Сувгийн хэсгийг салбараар сольсон.
 *
 * ҮҮРЭГ (2026-09-29 цэгцлэв): «хугацааны туршид ХЭР ЗАРСАН бэ». Нөөцийн
 * өртөг, гутал/аялал/ажилтны тоо энд БАЙХГҮЙ — тэд шүүлтүүрийг үл тоодог,
 * «Статистик»-т ижил тоогоор байгаа. Өмнө нь «Нөөц» хайрцаг зөвхөн
 * АГУУЛАХЫН өртгийг «Нөөцийн өртөг» гэж харуулж, Статистикийн бүх
 * нөөцийн тоотой зөрдөг байв.
 */

import { useQuery } from '@tanstack/react-query';
import { formatMntShort, formatQty } from '@boots/domain';
import { api, type Envelope } from '../lib/api';
import { useAuth } from '../lib/auth';
import { COST_ROLES, type Dashboard, type SalesReport } from '../lib/types';
import { ErrorNote, Spinner } from '../components/ui';
import { DataTable, type Column } from '../components/data-table';
import { SalesBlock } from '../components/reports/sales-block';

export function ReportsPage() {
  const { can } = useAuth();
  const showCost = can(...COST_ROLES);

  const dash = useQuery({
    queryKey: ['dashboard', 'month'],
    queryFn: () => api<Envelope<Dashboard>>('/reports/dashboard?period=month'),
  });
  // ⚠️ Түлхүүр `['sales-report']`-ээс ЯЛГААТАЙ — захиалга, санхүүгийн
  //    хуудас түүгээр `groupBy=day` хадгалдаг, cache хольж болохгүй.
  const sales = useQuery({
    queryKey: ['sales-report', 'month'],
    queryFn: () => api<Envelope<SalesReport>>('/reports/sales?groupBy=month'),
  });

  const err = dash.error ?? sales.error;
  if (err) return <ErrorNote>{(err as Error).message}</ErrorNote>;
  if (!dash.data || !sales.data) return <Spinner />;

  const d = dash.data.data;

  /*
    ⚠️ Картын ТОО хасагдсан (2026-09-29): «Агуулахын тайлан · N ш» нь
       зарагдсан гутлыг ч оруулсан бүх цагийн тоо байж «одоогийн үлдэгдэл»
       гэж уншигддаг, бусад хоёр нь Статистикийн тоог давтдаг байв. Тоо нь
       Статистикт; энд зөвхөн Excel-ээр татах тайлангийн жагсаалт.
  */
  const cards = [
    { name: 'Агуулахын тайлан', period: 'Одоогийн', note: 'Үлдэгдэл, байршил, өртөг', ready: true },
    { name: 'Худалдан авалтын тайлан', period: 'Бүх хугацаа', note: 'Нийлүүлэгч, аялал, дундаж өртөг', ready: true },
    { name: 'Ажилтны гүйцэтгэл', period: 'Бүх хугацаа', note: 'Хийсэн ажил, шошго, сүүлийн үйлдэл', ready: true },
    { name: 'Бөөний түншийн тайлан', period: '—', note: 'Лояалти, хөнгөлөлт, оноо · түншийн хүснэгт байхгүй', ready: false },
  ];

  return (
    <div className="flex flex-col gap-3">
      <SalesBlock />

      {/* ─────────── Сарын нэгтгэл ─────────── */}
      <section className="flex flex-col gap-2.5 rounded-card border border-hair bg-panel px-card py-4">
        <div className="flex flex-wrap items-baseline gap-3">
          <h2 className="text-base font-semibold">Сарын нэгтгэл</h2>
          <span className="text-[12.5px] text-ink-2">{new Date().getFullYear()} он</span>
          <span className="text-[11.5px] text-ink-3">дээрх шүүлтүүрээс үл хамаарна</span>
        </div>

        <DataTable
          rows={monthlyRows(d.intake.series, sales.data.data.groups)}
          columns={monthlyColumns(showCost)}
          rowKey={(m) => m.label}
        />

        <p className="text-[11.5px] text-ink-3">
          Зарсан, орлого, ашиг нь буцаагдсан мөрийг хассан ЦЭВЭР дүн.
          Ашиг нь зарах мөчид хөлдсөн нийт өртгөөс бодогдоно. «Аяллаар орсон» нь
          аяллын гутлын цуглуулсан огноогоор — эхний үлдэгдэл ОРОХГҮЙ (Статистик →
          Тойм-д тусдаа); Хуанлийн «Шинээр бүртгэгдсэн» нь системд үүссэн өдрөөр
          тул зөрж болно.
        </p>
      </section>

      {/* ─────────── Тайлангийн карт ─────────── */}
      <section className="flex flex-col gap-3.5 rounded-card border border-hair bg-panel px-card py-4">
        <div className="flex flex-wrap items-baseline gap-3">
          <h2 className="text-base font-semibold">Бэлэн тайлан</h2>
          <span className="text-[12.5px] text-ink-2">
            {formatQty(cards.filter((c) => c.ready).length)} / {formatQty(cards.length)} бэлэн
          </span>
          <span className="ml-auto text-[11.5px] text-ink-3">
            Excel рүү татах боломж удахгүй нэмэгдэнэ
          </span>
        </div>

        <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-3">
          {cards.map((c) => (
            <div
              key={c.name}
              className={`flex flex-col gap-2 rounded-well border px-4 py-3.5 transition ${
                c.ready ? 'border-hair bg-well-3 hover:border-accent-300' : 'border-hair bg-panel opacity-70'
              }`}
            >
              <div className="flex items-baseline gap-2.5">
                <span className="text-sm">{c.name}</span>
                <span
                  className={`ml-auto rounded-full px-2.5 py-[2px] text-[11px] whitespace-nowrap ${
                    c.ready ? 'bg-accent-100 text-accent-700' : 'bg-shell text-ink-2'
                  }`}
                >
                  {c.period}
                </span>
              </div>
              <span className="text-xs leading-[1.4] text-ink-2">{c.note}</span>
              <div className="flex items-baseline gap-2.5 pt-0.5">
                <button
                  type="button"
                  disabled
                  className="ml-auto cursor-not-allowed text-xs text-ink-3"
                  title="Excel рүү татах боломж хараахан бэлэн биш"
                >
                  Татах
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

    </div>
  );
}

/**
 * Сарын нэгтгэлийн мөр. Сарын дугаар нь `intake.series`-ийн ИНДЕКСЭЭС
 * гардаг тул нүд бүрт дахин тооцох боломжгүй — мөрийг урьдчилан бүрдүүлнэ.
 */
interface MonthRow {
  label: string;
  intake: number;
  units: number;
  /** Сервер Decimal-аар нэмсэн дүн. Борлуулалтгүй сард null. */
  revenueMnt: string | null;
  /** null = ашиг тооцох боломжгүй (бүх гутал өртөггүй, эсвэл эрхгүй). 0 БИШ. */
  profitMnt: string | null;
}

/** Сарын дүнг сервер (`groupBy=month`, түлхүүр `"2026-09"`) аль хэдийн нэмсэн. */
function monthlyRows(
  series: Dashboard['intake']['series'],
  groups: SalesReport['groups'],
): MonthRow[] {
  return series.map((m, i) => {
    const month = String(i + 1).padStart(2, '0');
    const g = groups.find((x) => x.key.slice(5, 7) === month);
    return {
      label: m.label,
      // Эхний үлдэгдэл сарын орлогод холилдохгүй (захиалагчийн цэгцлэл №2)
      intake: m.trip,
      units: g?.units ?? 0,
      revenueMnt: g?.revenueMnt ?? null,
      profitMnt: g?.profitMnt ?? null,
    };
  });
}

/** 640px-ээс доош `DataTable` эдгээрийг сар тус бүрийн карт болгоно. */
function monthlyColumns(showCost: boolean): Column<MonthRow>[] {
  return [
    { key: 'month', header: 'Сар', mobile: 'title', cell: (m) => `${m.label}-р сар` },
    {
      key: 'intake',
      // Хуанлийн «Шинээр бүртгэгдсэн» (үүссэн өдөр)-өөс ӨӨР суурьтай — нэрийг ялгав
      header: 'Аяллаар орсон',
      align: 'right',
      cell: (m) => (m.intake > 0 ? `${formatQty(m.intake)} ш` : '—'),
    },
    {
      key: 'units',
      header: 'Зарсан',
      align: 'right',
      mobile: 'value',
      cell: (m) => (m.units > 0 ? `${formatQty(m.units)} ш` : '—'),
    },
    {
      key: 'revenue',
      header: 'Орлого',
      align: 'right',
      cell: (m) => (m.units > 0 ? formatMntShort(m.revenueMnt) : '—'),
    },
    {
      key: 'profit',
      header: 'Ашиг',
      align: 'right',
      cell: (m) => {
        if (m.units === 0) return '—';
        if (!showCost) return '•••';
        // Тооцох боломжгүй ашгийг «0₮» гэж харуулбал ашиг олоогүй мэт ойлгогдоно
        return m.profitMnt === null ? '—' : formatMntShort(m.profitMnt);
      },
    },
  ];
}
